import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { ChevronLeft, Calendar, Loader, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const getBaseUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocal ? 'http://localhost:5000' : window.location.origin;
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background: #141414;
  color: #fff;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a2a;
  background: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackBtn = styled(Link)`
  color: #888;
  display: flex;
  align-items: center;
  text-decoration: none;
  font-size: 14px;
  &:hover { color: #fff; }
`;

const Title = styled.h2`
  font-size: 18px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Layout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 320px;
  border-right: 1px solid #2a2a2a;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
`;

const ListHeader = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: #888;
  border-bottom: 1px solid #2a2a2a;
`;

const SnapshotList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SnapshotItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #2a2a2a;
  cursor: pointer;
  background: ${p => p.$active ? '#1e3a8a' : 'transparent'};
  &:hover {
    background: ${p => p.$active ? '#1e3a8a' : '#222'};
  }
`;

const SnapTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
`;

const SnapMeta = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  justify-content: space-between;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: #141414;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  th, td {
    padding: 12px 16px;
    border-bottom: 1px solid #2a2a2a;
    text-align: right;
  }
  th {
    color: #888;
    font-size: 13px;
    background: #1a1a1a;
  }
  th:first-child, td:first-child { text-align: left; }
`;

export default function MoneyFlowHistoryPage() {
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnap, setSelectedSnap] = useState(null);
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        axios.get(`${getBaseUrl()}/api/moneyflow/history?limit=50`)
            .then(res => setSnapshots(res.data || []))
            .catch(err => console.error('Failed to load history', err))
            .finally(() => setLoading(false));
    }, []);

    const handleSelect = async (snap) => {
        setSelectedSnap(snap);
        setDetailsLoading(true);
        setDetails(null);
        try {
            const res = await axios.get(`${getBaseUrl()}/api/moneyflow/history/${snap.id}`);
            setDetails(res.data);
        } catch (err) {
            console.error('Failed to load snapshot details', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    return (
        <Page>
            <Header>
                <BackBtn to="/labs/moneyflow"><ChevronLeft size={18} /> 뒤로가기</BackBtn>
                <Title><Calendar size={18} color="#e50914" /> 자금 흐름 과거 기록 (Snapshots)</Title>
            </Header>
            <Layout>
                <Sidebar>
                    <ListHeader>저장된 스냅샷 목록 (최근 50개)</ListHeader>
                    {loading ? (
                        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>불러오는 중...</div>
                    ) : (
                        <SnapshotList>
                            {snapshots.map(s => (
                                <SnapshotItem 
                                    key={s.id} 
                                    $active={selectedSnap?.id === s.id}
                                    onClick={() => handleSelect(s)}
                                >
                                    <SnapTitle>
                                        [{s.type === 'value' ? '거래대금' : s.type === 'strength' ? '체결강도' : s.type}] {s.market}
                                    </SnapTitle>
                                    <SnapMeta>
                                        <span>{new Date(s.timestamp).toLocaleString()}</span>
                                        <span>{s.itemCount} 종목</span>
                                    </SnapMeta>
                                </SnapshotItem>
                            ))}
                            {snapshots.length === 0 && <div style={{ padding: 20, color: '#666', textAlign: 'center' }}>기록이 없습니다.</div>}
                        </SnapshotList>
                    )}
                </Sidebar>
                <MainContent>
                    {!selectedSnap ? (
                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                            좌측에서 조회할 스냅샷을 선택하세요
                        </div>
                    ) : detailsLoading ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}><Loader className="spin" /> 상세 데이터를 불러오는 중...</div>
                    ) : details && (
                        <div>
                            <h3 style={{ marginTop: 0 }}>
                                {details.date} {new Date(details.timestamp).toLocaleTimeString()} 기록
                            </h3>
                            <div style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>
                                종류: {details.type} | 시장: {details.market}
                            </div>
                            
                            <Table>
                                <thead>
                                    <tr>
                                        <th>순위</th>
                                        <th>종목명/분류</th>
                                        <th>현재가 (당시)</th>
                                        <th>통합 스코어 (100)</th>
                                        <th>대금/시총 (%)</th>
                                        <th>세부 순위</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.data && details.data.map((item, i) => (
                                        <tr key={item.code}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{item.name}</div>
                                                <div style={{ fontSize: 11, color: '#666' }}>{item.code}</div>
                                            </td>
                                            <td>{parseInt(item.price || 0).toLocaleString()}</td>
                                            <td>
                                                <div style={{ fontWeight: 700, color: '#f59e0b' }}>
                                                    {item.score?.toFixed(1)}점
                                                </div>
                                            </td>
                                            <td style={{ color: item.details?.ratioVOL > 0.001 ? '#34d399' : '#888', fontWeight: 600 }}>
                                                {item.details?.ratioVOL ? (item.details.ratioVOL * 100).toFixed(3) + '%' : '-'}
                                            </td>
                                            <td style={{ fontSize: '11px', color: '#888' }}>
                                                대금: {item.details?.volRank > 0 ? `${item.details.volRank}위` : '-'} | 
                                                체결: {item.details?.strRank > 0 ? `${item.details.strRank}위` : '-'} | 
                                                프그램: {item.details?.prgRank > 0 ? `${item.details.prgRank}위` : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </MainContent>
            </Layout>
        </Page>
    );
}
